import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getMediaUrl } from '../utils/mediaUrl';
import { videoSources } from '../utils/videoSources';
import { allowLandscape, lockPortrait } from '../utils/nativeBridge';

const STORY_DURATION_MS = 5000; // 5 seconds per image

const StoryViewerModal = ({
  isOpen,
  initialUserGroup,
  initialStoryIndex = 0,
  allUserGroups = [],
  onClose,
  onStoryDeleted,
}) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const toast = useToast();

  const [currentUserGroupIndex, setCurrentUserGroupIndex] = useState(0);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [quickReplyText, setQuickReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [showViewersModal, setShowViewersModal] = useState(false);
  const [viewersList, setViewersList] = useState([]);
  const [loadingViewers, setLoadingViewers] = useState(false);
  const [showHighlightSheet, setShowHighlightSheet] = useState(false);
  const [userHighlights, setUserHighlights] = useState([]);
  const [newHighlightTitle, setNewHighlightTitle] = useState('');
  const [savingHighlight, setSavingHighlight] = useState(false);

  const videoRef = useRef(null);
  const progressTimerRef = useRef(null);

  // Initialize group and story index from props
  useEffect(() => {
    if (initialUserGroup && allUserGroups.length > 0) {
      const idx = allUserGroups.findIndex((g) => g.userId === initialUserGroup.userId);
      setCurrentUserGroupIndex(idx > -1 ? idx : 0);
      setCurrentStoryIndex(initialStoryIndex || 0);
    }
  }, [initialUserGroup, initialStoryIndex, allUserGroups]);

  // Orientation handling: allow landscape while viewing story media, lock to portrait on close
  useEffect(() => {
    if (isOpen) {
      allowLandscape();
      return () => {
        lockPortrait();
      };
    }
  }, [isOpen]);

  const activeGroup = allUserGroups[currentUserGroupIndex] || initialUserGroup;
  const activeStory = activeGroup?.stories?.[currentStoryIndex];
  const isOwner = activeGroup?.userId === currentUser?.id;

  // Mark active story as viewed
  useEffect(() => {
    if (!activeStory?.id || isOwner) return;
    axios.post(`/api/stories/${activeStory.id}/view`).catch(console.warn);
  }, [activeStory?.id, isOwner]);

  // Navigate to next story or next user
  const handleNext = useCallback(() => {
    if (!activeGroup) return;

    if (currentStoryIndex < activeGroup.stories.length - 1) {
      // Next story in current group
      setCurrentStoryIndex((prev) => prev + 1);
      setProgress(0);
    } else if (currentUserGroupIndex < allUserGroups.length - 1) {
      // Next user group
      setCurrentUserGroupIndex((prev) => prev + 1);
      setCurrentStoryIndex(0);
      setProgress(0);
    } else {
      // Reached the end
      onClose();
    }
  }, [activeGroup, currentStoryIndex, currentUserGroupIndex, allUserGroups.length, onClose]);

  // Navigate to previous story or previous user
  const handlePrev = useCallback(() => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex((prev) => prev - 1);
      setProgress(0);
    } else if (currentUserGroupIndex > 0) {
      const prevGroup = allUserGroups[currentUserGroupIndex - 1];
      setCurrentUserGroupIndex((prev) => prev - 1);
      setCurrentStoryIndex(prevGroup.stories.length - 1);
      setProgress(0);
    }
  }, [currentStoryIndex, currentUserGroupIndex, allUserGroups]);

  // Auto-advance timer logic
  useEffect(() => {
    if (!isOpen || !activeStory || isPaused || showViewersModal) return;

    const duration =
      activeStory.mediaType === 'video' && videoRef.current?.duration
        ? videoRef.current.duration * 1000
        : STORY_DURATION_MS;

    const intervalStep = 50;
    const increment = (intervalStep / duration) * 100;

    progressTimerRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressTimerRef.current);
          handleNext();
          return 0;
        }
        return prev + increment;
      });
    }, intervalStep);

    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [isOpen, activeStory, isPaused, showViewersModal, handleNext]);

  // Fetch viewers list for owner
  const handleOpenViewers = async () => {
    if (!activeStory?.id) return;
    setIsPaused(true);
    setShowViewersModal(true);
    setLoadingViewers(true);
    try {
      const res = await axios.get(`/api/stories/${activeStory.id}/viewers`);
      setViewersList(res.data?.viewers || []);
    } catch (err) {
      console.error('Fetch viewers error:', err);
    } finally {
      setLoadingViewers(false);
    }
  };

  const handleOpenHighlightSheet = async () => {
    if (!currentUser) return;
    setIsPaused(true);
    setShowHighlightSheet(true);
    try {
      const res = await axios.get(`/api/stories/highlights/user/${currentUser.handle || currentUser.id}`);
      setUserHighlights(res.data?.highlights || []);
    } catch (err) {
      console.error('Fetch highlights error:', err);
    }
  };

  const handleAddToHighlight = async (highlightId) => {
    if (!activeStory?.id) return;
    setSavingHighlight(true);
    try {
      await axios.post(`/api/stories/highlights/${highlightId}/items`, {
        storyId: activeStory.id,
      });
      toast.success('Added to Highlight!');
      setShowHighlightSheet(false);
      setIsPaused(false);
    } catch (err) {
      toast.error('Failed to add to highlight');
    } finally {
      setSavingHighlight(false);
    }
  };

  const handleCreateAndAddToHighlight = async (e) => {
    e.preventDefault();
    if (!newHighlightTitle.trim() || !activeStory?.id) return;
    setSavingHighlight(true);
    try {
      const createRes = await axios.post('/api/stories/highlights', {
        title: newHighlightTitle.trim(),
        coverImage: activeStory.mediaUrl,
      });
      const newHl = createRes.data?.highlight;
      if (newHl?.id) {
        await axios.post(`/api/stories/highlights/${newHl.id}/items`, {
          storyId: activeStory.id,
        });
      }
      toast.success('Created and added to Highlight!');
      setNewHighlightTitle('');
      setShowHighlightSheet(false);
      setIsPaused(false);
    } catch (err) {
      toast.error('Failed to create highlight');
    } finally {
      setSavingHighlight(false);
    }
  };

  // Delete active story
  const handleDeleteStory = async () => {
    if (!activeStory?.id) return;
    try {
      await axios.delete(`/api/stories/${activeStory.id}`);
      toast.success(t('stories.deletedSuccess', 'Story deleted'));
      onStoryDeleted?.();
      handleNext();
    } catch (err) {
      console.error('Delete story error:', err);
      toast.error(t('stories.deleteFailed', 'Failed to delete story'));
    }
  };

  // Quick reply DM to story author
  const handleSendQuickReply = async (e) => {
    e.preventDefault();
    if (!quickReplyText.trim() || !activeGroup || sendingReply) return;

    setSendingReply(true);
    try {
      const replyMessage = `Replying to story: "${activeStory.caption || 'Attachment'}"\n\n${quickReplyText.trim()}`;
      await axios.post(`/api/messages/${activeGroup.userId}`, {
        content: replyMessage,
      });
      toast.success(t('stories.replySent', 'Reply sent in Messages!'));
      setQuickReplyText('');
    } catch (err) {
      console.error('Send quick reply error:', err);
      toast.error(t('stories.replyFailed', 'Failed to send reply'));
    } finally {
      setSendingReply(false);
    }
  };

  if (!isOpen || !activeGroup || !activeStory) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center select-none"
    >
      {/* Container simulating phone screen aspect ratio on desktop */}
      <div
        className="relative w-full max-w-sm sm:max-w-md h-full sm:h-[90vh] sm:max-h-[840px] sm:rounded-3xl overflow-hidden bg-black flex flex-col shadow-2xl"
        onPointerDown={() => setIsPaused(true)}
        onPointerUp={() => setIsPaused(false)}
      >
        {/* Progress Bar Segments */}
        <div className="absolute top-[env(safe-area-inset-top)] inset-x-0 z-20 flex gap-1.5 p-3 pointer-events-none">
          {activeGroup.stories.map((s, idx) => {
            let width = '0%';
            if (idx < currentStoryIndex) width = '100%';
            else if (idx === currentStoryIndex) width = `${progress}%`;

            return (
              <div
                key={s.id || idx}
                className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden"
              >
                <div
                  className="h-full bg-white transition-all duration-75"
                  style={{ width }}
                />
              </div>
            );
          })}
        </div>

        {/* Top Header: User info, timestamp, close & options */}
        <div className="absolute top-[calc(1.25rem+env(safe-area-inset-top))] inset-x-0 z-20 flex items-center justify-between px-4 py-2 pointer-events-auto">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full overflow-hidden border border-white/40 bg-zinc-800">
              {activeGroup.profilePicture ? (
                <img
                  src={getMediaUrl(activeGroup.profilePicture)}
                  alt={activeGroup.username}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white font-bold text-xs">
                  {(activeGroup.username || 'U').charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-1">
                <span className="font-bold text-white text-xs sm:text-sm drop-shadow-md">
                  {activeGroup.username}
                </span>
                {activeGroup.isVerified && (
                  <i className="fas fa-check-circle text-sky-400 text-xs" />
                )}
              </div>
              <span className="text-[10px] text-white/75 drop-shadow-xs">
                {t('stories.storyExpires', '24h Story')}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isOwner && (
              <button
                type="button"
                onClick={handleDeleteStory}
                className="p-2 text-white/80 hover:text-red-400 rounded-full hover:bg-white/10 transition-colors"
                title="Delete story"
              >
                <i className="far fa-trash-alt text-sm" />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-colors"
              aria-label="Close story"
            >
              <i className="fas fa-times text-base" />
            </button>
          </div>
        </div>

        {/* Media Display Area with Click Navigation */}
        <div className="flex-1 relative w-full h-full flex items-center justify-center bg-black overflow-hidden">
          {activeStory.mediaType === 'video' ? (
            <video
              key={activeStory.mediaUrl}
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-full object-contain"
            >
              {videoSources(activeStory.mediaUrl)}
            </video>
          ) : (
            <img
              src={getMediaUrl(activeStory.mediaUrl)}
              alt="Story"
              className="w-full h-full object-contain"
            />
          )}

          {/* Left / Right Tap zones */}
          <div
            className="absolute inset-y-0 left-0 w-1/3 z-10 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              handlePrev();
            }}
          />
          <div
            className="absolute inset-y-0 right-0 w-2/3 z-10 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
          />

          {/* Story Caption */}
          {activeStory.caption && (
            <div className="absolute bottom-20 inset-x-0 z-20 px-4 py-2.5 text-center">
              <span className="inline-block px-4 py-2 rounded-2xl bg-black/60 backdrop-blur-md text-white text-sm font-medium leading-relaxed max-w-[90%] shadow-lg">
                {activeStory.caption}
              </span>
            </div>
          )}
        </div>

        {/* Bottom Bar: Owner Viewers or Viewer Quick Reply */}
        <div className="absolute bottom-0 inset-x-0 z-20 px-3 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-auto">
          {isOwner ? (
            // Owner View: Viewers Count Button & Highlight Button
            <div className="flex items-center justify-between px-2 w-full">
              <button
                type="button"
                onClick={handleOpenViewers}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white text-xs font-semibold backdrop-blur-md transition-colors"
              >
                <i className="far fa-eye text-xs" />
                <span>
                  {activeStory.viewsCount || 0} {t('stories.views', 'viewers')}
                </span>
              </button>

              <button
                type="button"
                onClick={handleOpenHighlightSheet}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white text-xs font-semibold backdrop-blur-md transition-colors hover:scale-105 active:scale-95"
              >
                <i className="far fa-star text-amber-300" />
                <span>Highlight</span>
              </button>
            </div>
          ) : (
            // Viewer View: Quick Reply Input to Author's DMs
            <form onSubmit={handleSendQuickReply} className="flex items-center gap-2">
              <input
                type="text"
                value={quickReplyText}
                onChange={(e) => setQuickReplyText(e.target.value)}
                placeholder={t('stories.replyPlaceholder', 'Send a reply...')}
                className="flex-1 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white placeholder-white/60 text-xs focus:outline-none focus:bg-white/30 focus:border-white/60 transition-all"
              />
              <button
                type="submit"
                disabled={!quickReplyText.trim() || sendingReply}
                className="w-8 h-8 rounded-full bg-sky-500 hover:bg-sky-600 text-white flex items-center justify-center active:scale-95 disabled:opacity-40 transition-all flex-shrink-0 shadow-xs"
                title="Send reply"
              >
                {sendingReply ? (
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <i className="fas fa-paper-plane text-xs" />
                )}
              </button>
            </form>
          )}
        </div>

        {/* Viewers Sheet Modal */}
        {showViewersModal && (
          <div
            className="absolute inset-0 z-30 bg-black/85 backdrop-blur-md flex flex-col justify-end animate-in slide-in-from-bottom duration-200"
            onClick={() => {
              setShowViewersModal(false);
              setIsPaused(false);
            }}
          >
            <div
              className="bg-zinc-900 rounded-t-3xl border-t border-zinc-800 p-4 max-h-[60%] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between pb-3 border-b border-zinc-800">
                <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
                  <i className="far fa-eye text-sky-400" />
                  <span>
                    {t('stories.viewersTitle', 'Story Viewers')} ({viewersList.length})
                  </span>
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowViewersModal(false);
                    setIsPaused(false);
                  }}
                  className="text-zinc-400 hover:text-white p-1"
                >
                  <i className="fas fa-times text-xs" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto divide-y divide-zinc-800/60 py-2">
                {loadingViewers ? (
                  <div className="py-8 flex justify-center text-zinc-500">
                    <div className="w-5 h-5 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : viewersList.length === 0 ? (
                  <p className="text-center py-6 text-xs text-zinc-500">
                    {t('stories.noViewersYet', 'No viewers yet')}
                  </p>
                ) : (
                  viewersList.map((viewer) => (
                    <div key={viewer.id} className="flex items-center gap-3 py-2.5">
                      <div className="w-8 h-8 rounded-full overflow-hidden bg-zinc-800 flex-shrink-0">
                        {viewer.profilePicture ? (
                          <img
                            src={getMediaUrl(viewer.profilePicture)}
                            alt={viewer.username}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white text-xs font-bold">
                            {(viewer.username || 'U').charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-white truncate">
                          {viewer.username}
                        </p>
                        <p className="text-[10px] text-zinc-400 truncate">
                          @{viewer.handle?.replace(/^@+/, '')}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add to Highlight Sheet Modal */}
        {showHighlightSheet && (
          <div
            className="absolute inset-0 z-30 bg-black/85 backdrop-blur-md flex flex-col justify-end animate-in slide-in-from-bottom duration-200"
            onClick={() => {
              setShowHighlightSheet(false);
              setIsPaused(false);
            }}
          >
            <div
              className="bg-zinc-900 rounded-t-3xl border-t border-zinc-800 p-5 max-h-[70%] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between pb-3 border-b border-zinc-800">
                <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
                  <i className="far fa-star text-amber-400" />
                  <span>Save to Story Highlight</span>
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowHighlightSheet(false);
                    setIsPaused(false);
                  }}
                  className="text-zinc-400 hover:text-white p-1"
                >
                  <i className="fas fa-times text-xs" />
                </button>
              </div>

              {/* Existing Highlights to tap */}
              <div className="py-4">
                <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-3">
                  Choose a Highlight Album
                </p>
                <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-none">
                  {userHighlights.map((hl) => (
                    <button
                      key={hl.id}
                      type="button"
                      disabled={savingHighlight}
                      onClick={() => handleAddToHighlight(hl.id)}
                      className="flex flex-col items-center flex-shrink-0 group active:scale-95 transition"
                    >
                      <div className="w-14 h-14 rounded-full p-0.5 border-2 border-zinc-700 group-hover:border-amber-400 bg-zinc-800 flex items-center justify-center overflow-hidden">
                        {hl.coverImage ? (
                          <img src={getMediaUrl(hl.coverImage)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-lg">⭐</span>
                        )}
                      </div>
                      <span className="text-[11px] font-medium text-zinc-300 mt-1.5 max-w-[64px] truncate text-center">
                        {hl.title}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Or Create a New One */}
              <form onSubmit={handleCreateAndAddToHighlight} className="pt-2 border-t border-zinc-800">
                <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-2">
                  Or Create New Highlight
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newHighlightTitle}
                    onChange={(e) => setNewHighlightTitle(e.target.value)}
                    placeholder="New Highlight Name..."
                    maxLength={30}
                    className="flex-1 px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white placeholder-zinc-500 text-xs focus:outline-none focus:border-amber-400"
                  />
                  <button
                    type="submit"
                    disabled={!newHighlightTitle.trim() || savingHighlight}
                    className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold text-xs transition"
                  >
                    {savingHighlight ? 'Saving...' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryViewerModal;
